import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DropdownAnchor } from './JourneyPickerDesktop.styles';

const VIEWPORT_MARGIN = 16;
const OFFSET_FROM_TRIGGER = 8;

export function ClampedDropdownAnchor({ wide, children }: { wide?: boolean; children: ReactNode }) {
  const placeholderRef = useRef<HTMLSpanElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const compute = useCallback(() => {
    const placeholder = placeholderRef.current;
    const dropdown = dropdownRef.current;
    if (!placeholder || !dropdown || typeof window === 'undefined') return;
    const trigger = placeholder.parentElement;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const dropdownRect = dropdown.getBoundingClientRect();
    const bar = trigger.closest('[data-journey-picker-bar]');
    const anchorRect = bar ? bar.getBoundingClientRect() : triggerRect;

    let left = anchorRect.left + anchorRect.width / 2 - dropdownRect.width / 2;

    const maxLeft = window.innerWidth - VIEWPORT_MARGIN - dropdownRect.width;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

    const top = triggerRect.bottom + OFFSET_FROM_TRIGGER;

    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  const schedule = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      compute();
    });
  }, [compute]);

  useLayoutEffect(() => {
    compute();
    const dropdown = dropdownRef.current;
    const bar = placeholderRef.current?.parentElement?.closest('[data-journey-picker-bar]');
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    if (dropdown) observer?.observe(dropdown);
    if (bar) observer?.observe(bar);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [compute, schedule]);

  if (typeof document === 'undefined') return null;

  const style: CSSProperties = {
    top: pos?.top ?? 0,
    left: pos?.left ?? 0,
    visibility: pos ? 'visible' : 'hidden',
  };

  return (
    <>
      <span ref={placeholderRef} style={{ display: 'none' }} aria-hidden />
      {createPortal(
        <DropdownAnchor
          ref={dropdownRef}
          data-journey-picker-dropdown="true"
          data-wide={wide ? 'true' : undefined}
          style={style}
        >
          {children}
        </DropdownAnchor>,
        document.body
      )}
    </>
  );
}
